/**
 * Market Radar symbol detail.
 *
 * Read-only. Shows the current scored state, its constituent metrics, and the
 * three recorded transition histories, plus a small native-SVG chart of
 * activityScore against rankingScore over time. No charting dependency: the
 * point is operational validation, not a trading terminal.
 */

import React from "react";
import {
  useMarketRadarHistory,
  useMarketRadarSymbol,
  type RadarHistoryRow,
} from "@/hooks/useMarketRadar";
import {
  COLORS,
  DataQualityBadge,
  ExecutabilityCell,
  LifecycleBadge,
  NotMeasured,
  TierBadge,
  ageLabel,
  num,
  price,
  turnover,
} from "./radarFormat";

function timeLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        color: COLORS.accent, fontSize: 10, fontWeight: 800, letterSpacing: "0.1em",
        textTransform: "uppercase", marginBottom: 6, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 3,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function MetricRow({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "2px 0", fontSize: 11 }}>
      <span style={{ color: COLORS.dim }} title={hint}>{label}</span>
      <span style={{ color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

/**
 * Two-series line chart, drawn directly as SVG.
 *
 * Both series share one scale so the lag between the raw measurement and the
 * smoothed ranking series is visible — that gap is the whole reason the two
 * exist separately.
 */
function ScoreChart({ rows }: { rows: RadarHistoryRow[] }) {
  const ordered = [...rows].sort((left, right) => left.bucketStartMs - right.bucketStartMs);
  const points = ordered.filter((row) => row.activityScore !== null || row.rankingScore !== null);

  if (points.length < 2) {
    return (
      <div style={{ color: COLORS.fainter, fontSize: 11, padding: "10px 0" }}>
        Not enough recorded history yet to plot. Rows appear once the radar has ranked this symbol
        across several one-minute buckets.
      </div>
    );
  }

  const width = 560;
  const height = 120;
  const padding = { top: 8, right: 8, bottom: 16, left: 30 };

  const values: number[] = [];
  for (const row of points) {
    if (row.activityScore !== null) values.push(row.activityScore);
    if (row.rankingScore !== null) values.push(row.rankingScore);
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const x = (index: number) => padding.left
    + (index / (points.length - 1)) * (width - padding.left - padding.right);
  const y = (value: number) => padding.top
    + (1 - (value - min) / span) * (height - padding.top - padding.bottom);

  /** Break the path wherever a value is missing rather than interpolating. */
  const path = (pick: (row: RadarHistoryRow) => number | null) => {
    let d = "";
    let pen = false;
    points.forEach((row, index) => {
      const value = pick(row);
      if (value === null || !Number.isFinite(value)) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(index).toFixed(1)},${y(value).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  };

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="activityScore and rankingScore over time" style={{ display: "block" }}>
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#222" />
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#222" />
        <text x={2} y={padding.top + 8} fill={COLORS.fainter} fontSize={8}>{max.toFixed(1)}</text>
        <text x={2} y={height - padding.bottom} fill={COLORS.fainter} fontSize={8}>{min.toFixed(1)}</text>
        <path d={path((row) => row.activityScore)} fill="none" stroke="#66aaff" strokeWidth={1} />
        <path d={path((row) => row.rankingScore)} fill="none" stroke={COLORS.accent} strokeWidth={1.5} />
        <text x={padding.left} y={height - 4} fill={COLORS.fainter} fontSize={8}>
          {timeLabel(points[0].bucketStartMs)}
        </text>
        <text x={width - padding.right} y={height - 4} fill={COLORS.fainter} fontSize={8} textAnchor="end">
          {timeLabel(points[points.length - 1].bucketStartMs)}
        </text>
      </svg>
      <div style={{ display: "flex", gap: 14, fontSize: 10, color: COLORS.dim, marginTop: 2 }}>
        <span><span style={{ color: "#66aaff" }}>—</span> activityScore (raw measurement)</span>
        <span><span style={{ color: COLORS.accent }}>—</span> rankingScore (smoothed, drives rank)</span>
      </div>
    </div>
  );
}

export default function RadarSymbolDetail({ symbol, onClose }: { symbol: string; onClose: () => void }) {
  const { data, isError, error } = useMarketRadarSymbol(symbol);
  const history = useMarketRadarHistory(symbol, "1m");

  const entry = data?.symbol;

  return (
    <div
      data-testid="radar-symbol-detail"
      style={{
        border: `1px solid ${COLORS.border}`,
        background: "#0d0d0d",
        padding: 14,
        marginBottom: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: COLORS.text, fontSize: 16, fontWeight: 800, letterSpacing: "0.04em" }}>{symbol}</span>
          {entry && <LifecycleBadge state={entry.lifecycleState} />}
          {entry && <TierBadge tier={entry.tier} />}
          {entry && <DataQualityBadge quality={entry.dataQuality} />}
          {entry?.rank !== null && entry !== undefined && (
            <span style={{ color: COLORS.dim, fontSize: 11 }}>rank #{entry.rank}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent", border: `1px solid #333`, color: COLORS.dim,
            fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", cursor: "pointer",
          }}
        >
          CLOSE
        </button>
      </div>

      {isError && (
        <div style={{ color: COLORS.warn, fontSize: 11, marginTop: 10 }}>
          {String(error).includes("404")
            ? "This symbol is not currently ranked by the radar."
            : "Symbol detail is unavailable."}
        </div>
      )}

      {entry && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
          <div>
            <Section title="Scores">
              <MetricRow label="activityScore (raw)" value={num(entry.activityScore, 3)} hint="Immediate activity measurement" />
              <MetricRow label="rankingScore (smoothed)" value={num(entry.rankingScore, 3)} hint="Drives rank and tier selection" />
              <MetricRow label="lifecycle smoothed" value={num(entry.smoothedActivityScore, 3)} hint="The lifecycle machine's own series" />
              <MetricRow label="rank / previous" value={`${entry.rank ?? "—"} / ${entry.previousRank ?? "—"}`} />
              <MetricRow
                label="executable"
                value={<ExecutabilityCell score={entry.executability.score} executable={entry.executability.executable} reasons={entry.executability.reasons} />}
                hint="Liquidity only — deliberately separate from activity"
              />
            </Section>

            <Section title="Metrics">
              <MetricRow label="last price" value={price(entry.lastPrice)} hint="Display context only, never a score input" />
              <MetricRow label="24h turnover" value={turnover(entry.turnover24hQuote)} hint="Eligibility gate only, never a score input" />
              <MetricRow label="volume acceleration" value={num(entry.metrics.volumeAcceleration, 2, "x")} />
              <MetricRow label="trade rate" value={num(entry.metrics.tradeRate, 2, "/s")} />
              <MetricRow label="trade rate accel" value={num(entry.metrics.tradeRateAcceleration, 2, "x")} />
              <MetricRow label="micro volatility" value={num(entry.metrics.microVolatility, 4)} />
              <MetricRow label="price velocity" value={num(entry.metrics.priceVelocity, 2)} />
              <MetricRow label="range expansion" value={num(entry.metrics.rangeExpansionRatio, 2, "x")} />
              <MetricRow label="relative turnover share" value={num(entry.metrics.relativeTurnoverShare, 2, "x")} />
              <MetricRow label="spread" value={num(entry.metrics.spreadBps, 2, " bps")} />
              <MetricRow label="book imbalance" value={num(entry.metrics.bookImbalance, 3)} />
              <MetricRow label="depth quality" value={num(entry.metrics.depthQuality, 2)} />
              <MetricRow label="aggressor ratio" value={num(entry.metrics.aggressorRatio, 2)} />
              <MetricRow label="liquidity stability" value={num(entry.metrics.liquidityStability, 2)} />
              <MetricRow label="first detected" value={new Date(entry.firstDetectedAt).toLocaleString()} />
              <MetricRow label="state changed" value={new Date(entry.stateChangedAt).toLocaleString()} />
            </Section>
          </div>

          <div>
            <Section title="Recent scores (1m buckets)">
              {history.isError
                ? <div style={{ color: COLORS.fainter, fontSize: 11 }}>History is unavailable (persistence may be disabled).</div>
                : <ScoreChart rows={history.data?.rows ?? []} />}
            </Section>

            <Section title="Lifecycle transitions">
              {(data.lifecycleTransitions ?? []).length === 0
                ? <div style={{ color: COLORS.fainter, fontSize: 11 }}>None recorded.</div>
                : (data.lifecycleTransitions ?? []).slice(0, 8).map((row, index) => (
                  <div key={`${row.atMs}-${index}`} style={{ fontSize: 11, color: COLORS.dim, padding: "2px 0" }}>
                    <span style={{ color: COLORS.fainter }}>{timeLabel(row.atMs)}</span>{" "}
                    {row.fromState ?? "—"} <span style={{ color: COLORS.fainter }}>→</span>{" "}
                    <span style={{ color: COLORS.text }}>{row.toState}</span>
                    {row.rank !== null && <span style={{ color: COLORS.fainter }}> @#{row.rank}</span>}
                  </div>
                ))}
            </Section>

            <Section title="Tier transitions">
              {(data.tierTransitions ?? []).length === 0
                ? <div style={{ color: COLORS.fainter, fontSize: 11 }}>None recorded.</div>
                : (data.tierTransitions ?? []).slice(0, 8).map((row, index) => (
                  <div key={`${row.atMs}-${index}`} style={{ fontSize: 11, color: COLORS.dim, padding: "2px 0" }}>
                    <span style={{ color: COLORS.fainter }}>{timeLabel(row.atMs)}</span>{" "}
                    {row.fromTier} <span style={{ color: COLORS.fainter }}>→</span>{" "}
                    <span style={{ color: COLORS.text }}>{row.toTier}</span>
                    {row.rank !== null && <span style={{ color: COLORS.fainter }}> @#{row.rank}</span>}
                  </div>
                ))}
            </Section>

            <Section title="Eligibility transitions">
              {(data.eligibilityTransitions ?? []).length === 0
                ? (
                  <div style={{ color: COLORS.fainter, fontSize: 11 }}>
                    None recorded. Observed eligibility history only — the radar records changes for
                    symbols it has evaluated, not a full-universe audit.
                  </div>
                )
                : (data.eligibilityTransitions ?? []).slice(0, 8).map((row, index) => (
                  <div key={`${row.atMs}-${index}`} style={{ fontSize: 11, color: COLORS.dim, padding: "2px 0" }}>
                    <span style={{ color: COLORS.fainter }}>{timeLabel(row.atMs)}</span>{" "}
                    <span style={{ color: row.toEligible ? COLORS.good : "#c86464" }}>
                      {row.toEligible ? "became eligible" : "lost eligibility"}
                    </span>
                    {row.reasons.length > 0 && (
                      <span style={{ color: COLORS.fainter }}> ({row.reasons.join(", ")})</span>
                    )}
                    {row.turnover24hQuote !== null && (
                      <span style={{ color: COLORS.fainter }}> 24h {turnover(row.turnover24hQuote)}</span>
                    )}
                  </div>
                ))}
            </Section>
          </div>
        </div>
      )}

      {!entry && !isError && (
        <div style={{ color: COLORS.fainter, fontSize: 11, marginTop: 10 }}>Loading symbol detail…</div>
      )}

      {entry && (
        <div style={{ color: COLORS.fainter, fontSize: 10, marginTop: 12 }}>
          Updated {ageLabel(Date.now() - Date.parse(entry.updatedAt))} ago.
          {entry.dataQuality === "scan" && (
            <span style={{ marginLeft: 6 }}>
              Trade, aggressor and book metrics show <NotMeasured /> because this symbol is observed by
              REST ticker polling only — that is an observation limit, not market inactivity.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
